import React, { useEffect, useState } from "react";
import { portalApi, formatPortalError, getPortalToken } from "@/api/portalClient";
import { Loader2, Receipt, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const eur = (n) => new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR" }).format(Number(n || 0));

export default function EmployeePortalPayslips() {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await portalApi.get("/employee-portal/me/payslips");
        setPayslips(r.data || []);
      } catch (e) { toast.error(formatPortalError(e)); }
      setLoading(false);
    })();
  }, []);

  const downloadPdf = async (ps) => {
    try {
      const r = await portalApi.get(`/employee-portal/me/payslips/${ps.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `palkkalaskelma_${ps.payslip_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 1000);
      toast.success("PDF ladattu");
    } catch (e) { toast.error(formatPortalError(e)); }
  };

  if (loading) return <div className="text-center py-12 text-white/40"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="emp-portal-payslips">
      <div>
        <h1 className="text-2xl font-display gradient-text-luxe">Omat palkkalaskelmat</h1>
        <p className="text-xs text-white/40 mt-1">Kaikki maksetut palkkalaskelmat. Lataa PDF tai napsauta yksityiskohtia varten.</p>
      </div>

      {payslips.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center text-white/40">
          <Receipt className="w-10 h-10 mx-auto mb-2" />
          <p className="text-sm">Ei vielä palkkalaskelmia</p>
        </div>
      ) : (
        <div className="space-y-2">
          {payslips.map(p => (
            <div key={p.id} className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={`payslip-${p.id}`}>
              <button
                onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-white/[0.04] transition text-left"
              >
                <div className="min-w-0">
                  <p className="text-sm text-white">
                    <span className="font-mono text-white/40 text-xs mr-2">{p.payslip_number}</span>
                    Kausi {p.pay_period_start} – {p.pay_period_end}
                  </p>
                  <p className="text-xs text-white/50 mt-1">Maksupäivä {p.pay_date}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-widest text-white/40">Netto</p>
                    <p className="text-xl font-display text-emerald-300 font-mono">{eur(p.net_total)}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); downloadPdf(p); }} className="border-amber-400/30 inline-flex items-center gap-1 text-amber-200 hover:bg-amber-500/10" data-testid={`pdf-${p.id}`}>
                    <Download className="w-3.5 h-3.5" /> PDF
                  </Button>
                </div>
              </button>
              {expanded === p.id && (
                <div className="border-t border-white/5 p-4 bg-black/20">
                  <PayslipDetail payslip={p} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const EARNING_LABELS = {
  base_salary: "Peruspalkka",
  overtime: "Ylityökorvaus",
  sunday_bonus: "Sunnuntaityölisä",
  evening_bonus: "Iltatyölisä",
  night_bonus: "Yötyölisä",
  holiday_bonus: "Lomaraha",
  vacation_pay: "Lomapalkka",
  other_taxable: "Muut veronalaiset",
};

function PayslipDetail({ payslip }) {
  const e = payslip.earnings || {};
  const d = payslip.deductions || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-amber-200/70 mb-2">Ansiot</p>
        {Object.entries(e).filter(([, v]) => v > 0).map(([k, v]) => (
          <Row key={k} label={EARNING_LABELS[k] || k} value={eur(v)} />
        ))}
        <Row label="Rahapalkka yht." value={eur(payslip.earnings_total)} bold />
        {payslip.benefits_total > 0 && <Row label="Luontoisedut" value={eur(payslip.benefits_total)} />}
        <Row label="BRUTTO" value={eur(payslip.gross_total)} bold accent="text-blue-300" />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-red-300/70 mb-2">Vähennykset</p>
        <Row label={`Ennakonpidätys (${(d.tax_base_percent_used || 0).toFixed(1)}% + ${(d.tax_additional_percent_used || 0).toFixed(1)}%)`} value={eur(d.tax_withholding)} neg />
        <Row label={`TyEL ${d.tyel_percent_used || 0}%`} value={eur(d.tyel)} neg />
        <Row label={`TVR ${d.unemployment_percent_used || 0}%`} value={eur(d.unemployment)} neg />
        <Row label={`SaVa ${d.health_insurance_percent_used || 0}%`} value={eur(d.health_insurance)} neg />
        <Row label="Vähennykset yht." value={eur(payslip.deductions_total)} bold neg />
        <Row label="NETTO" value={eur(payslip.net_total)} bold accent="text-emerald-300" big />
      </div>
      {payslip.ytd && (
        <div className="md:col-span-2 border-t border-white/5 pt-3">
          <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Vuosikertymät (sis. tämä)</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <YtdItem label="Brutto YTD" value={eur(payslip.ytd.gross)} />
            <YtdItem label="Ennakonpidätys YTD" value={eur(payslip.ytd.tax)} accent="text-red-300" />
            <YtdItem label="TyEL YTD" value={eur(payslip.ytd.tyel)} />
            <YtdItem label="Netto YTD" value={eur(payslip.ytd.net)} accent="text-emerald-300" />
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, bold = false, neg = false, accent = "", big = false }) {
  return (
    <div className={`flex justify-between items-center py-0.5 ${big ? "text-base py-1" : ""} ${bold ? "font-semibold" : ""}`}>
      <span className={accent || "text-white/70"}>{label}</span>
      <span className={`font-mono ${neg ? "text-red-300" : accent || "text-white"}`}>{neg ? "-" : ""}{value}</span>
    </div>
  );
}
function YtdItem({ label, value, accent = "text-white/80" }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
      <p className={`font-mono mt-1 ${accent}`}>{value}</p>
    </div>
  );
}
